import { useEffect, useMemo, useRef, useState } from 'react';
import { useRoute } from '@react-navigation/native';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import {
  getMachines,
  configureOperations,
  getMachineOperationById,
  selectMachines,
  selectMachinesLoaded,
  selectMachinesOperations,
  selectMachinesOperationsLoaded,
} from '../../../../../../../reducers/machines';
import { getStateParams, navigateBack, navigateTo, navigationRef, OPERATION_ACTION } from '@helpers';
import { Operation, OperationsMachine } from '@interfaces/machines';

export const useManageMachinesOperationDetails = () => {
  const route = useRoute();
  const dispatch = useAppDispatch();

  const machinesOperations = useSelector(selectMachinesOperations);
  const machines = useSelector(selectMachines);
  const machinesLoaded = useSelector(selectMachinesLoaded);
  const machinesOperationsLoaded = useSelector(selectMachinesOperationsLoaded);

  const id = getStateParams(route).id;

  const currentOperation = useMemo(() => machinesOperations.find((d: Operation) => d.operationId === id), [machinesOperations, id]);
  const [operation, setOperation] = useState<Operation | null>(currentOperation ?? null);

  const derivedSelectedMachines = useMemo(() => operation?.machines?.map((i: OperationsMachine) => i.machineId) ?? [], [operation?.machines]);

  const initialSelectedList = useRef<number[]>(derivedSelectedMachines);
  const [selectedList, setSelectedList] = useState<number[]>(derivedSelectedMachines);
  const processingOperations = useRef<number[]>([]);

  const onSelectMachine = (machineId: number) => {
    if (selectedList.includes(machineId)) {
      setSelectedList((prev) => prev.filter((id) => id !== machineId));
      return;
    }
    setSelectedList((prev) => [...prev, machineId]);
  };

  const onSelectedListChange = async (controller: AbortController) => {
    const addedMachinesActions = selectedList
      .filter((id) => !initialSelectedList.current.includes(id))
      .map((id) => ({ machineId: id, action: OPERATION_ACTION.LINK }));

    const removedMachinesActions = initialSelectedList.current
      .filter((id) => !selectedList.includes(id))
      .map((id) => ({ machineId: id, action: OPERATION_ACTION.UNLINK }));

    const actions = [...addedMachinesActions, ...removedMachinesActions];
    const shouldApiCallBeInvoked = [...selectedList].sort().join() !== [...initialSelectedList.current].sort().join();

    if (!shouldApiCallBeInvoked || !operation) return;

    processingOperations.current = actions.map((a) => a.machineId);

    await dispatch(
      configureOperations({
        operationId: operation.operationId,
        machines: actions,
        signal: controller.signal,
      })
    );

    initialSelectedList.current = selectedList;
    processingOperations.current = [];
  };

  useEffect(() => {
    let cancelled = false;

    if (!operation && id != null) {
      (async () => {
        const found = await dispatch(getMachineOperationById(id));
        if (cancelled) return;
        if (!found) {
          navigateBack();
          return;
        }
        setOperation(found);
        const selectedMachines = found.machines?.map((i: OperationsMachine) => i.machineId) ?? [];
        initialSelectedList.current = selectedMachines;
        setSelectedList(selectedMachines);
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [!!operation, id]);

  useEffect(() => {
    if (!machinesLoaded) {
      dispatch(getMachines());
    }
  }, [machinesLoaded]);

  const loaded = machinesLoaded && (machinesOperationsLoaded || !!operation);
  const isOperationsProcessing = machinesLoaded && !!operation && !machinesOperationsLoaded;

  const onBackButtonPressed = () => {
    if (navigationRef?.current?.canGoBack()) {
      navigateBack();
      return;
    }

    navigateTo('Settings', {
      screen: 'Machines',
      params: {
        screen: 'MachinesScreen',
        params: {
          screen: 'MachinesOperations',
        },
      },
    });
  };

  return {
    operation,
    machines,
    selectedList,
    loaded,
    isOperationsProcessing,
    processingOperations,
    onSelectMachine,
    onSelectedListChange,
    onBackButtonPressed,
  };
};
